import { AnchorProvider as Provider } from "@coral-xyz/anchor";
import { WalletLoadError } from "@solana/wallet-adapter-base";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Connection, PublicKey } from "@solana/web3.js";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { DEFAULT_ENDPOINT } from "utils/web3/endpoints";

import { useWalletInfo } from "./WalletInfoContext";

export const defaultAnchorProvider = () => {
  return new Provider(
    new Connection(DEFAULT_ENDPOINT.endpoint, "recent"),
    {
      signTransaction: () => {
        throw new WalletLoadError();
      },
      signAllTransactions: () => {
        throw new WalletLoadError();
      },
      publicKey: PublicKey.default,
    },
    Provider.defaultOptions()
  );
};

const AnchorContext = createContext<{
  anchorProvider: Provider;
}>({
  anchorProvider: defaultAnchorProvider(),
});

export const AnchorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { connection } = useConnection();
  const { walletAddress } = useWalletInfo();
  const { signTransaction, signAllTransactions, publicKey } = useWallet();

  const anchorProvider = useMemo(
    () =>
      new Provider(
        connection,
        signTransaction && signAllTransactions && publicKey
          ? { signTransaction, signAllTransactions, publicKey }
          : {
              signTransaction: () => {
                throw new WalletLoadError();
              },
              signAllTransactions: () => {
                throw new WalletLoadError();
              },
              publicKey: walletAddress ?? PublicKey.default,
            },
        Provider.defaultOptions()
      ),
    [connection, publicKey, walletAddress, signAllTransactions, signTransaction]
  );
  return (
    <AnchorContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        anchorProvider,
      }}
    >
      {children}
    </AnchorContext.Provider>
  );
};

export function useAnchorProvider(): Provider {
  return useContext(AnchorContext).anchorProvider;
}
